import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Typography
} from '@mui/material';

import { version as appVersion } from '../../../package.json';
import changelog from '../../generated/changelog';
import { ChangelogVersion, parseChangelogBlock, splitChangelogBlocks } from '../../utils/parseChangelog';

const PAGE_SIZE = 3;

interface ReleaseNotesModalProps {
  open: boolean;
  onClose: () => void;
  version?: string;
}

function formatChangelogLine(text: string) {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1');
}

function ReleaseNotesContent({ notes, current }: { notes: ChangelogVersion; current: boolean }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Versão
        {' '}
        {notes.version}
        {current ? ' · atual' : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Publicada em
        {' '}
        {notes.date}
      </Typography>

      {notes.sections.map((section) => (
        <Box key={section.id} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {section.title}
          </Typography>

          {section.items.map((item) => (
            <Box key={item.id} sx={{ mb: 2, pl: 1 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                {item.title}
              </Typography>
              {item.lines.map((line) => (
                <Typography
                  key={`${item.id}-${line}`}
                  variant="body2"
                  component="div"
                  sx={{ pl: line.startsWith('-') ? 2 : 0, mb: 0.25 }}
                >
                  {formatChangelogLine(line.replace(/^-\s*/, ''))}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default function ReleaseNotesModal({
  open,
  onClose,
  version = appVersion
}: ReleaseNotesModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const blocks = useMemo(() => splitChangelogBlocks(changelog), []);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const notes = useMemo(
    () => blocks
      .slice(0, visibleCount)
      .map((block) => parseChangelogBlock(block))
      .filter((item): item is ChangelogVersion => item !== null),
    [blocks, visibleCount]
  );
  const hasMore = visibleCount < blocks.length;

  useEffect(() => {
    if (open) setVisibleCount(PAGE_SIZE);
  }, [open]);

  useEffect(() => {
    if (!open || !hasMore) return undefined;

    const root = contentRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setVisibleCount((current) => Math.min(current + PAGE_SIZE, blocks.length));
      }
    }, { root, rootMargin: '120px' });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [open, hasMore, visibleCount, blocks.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="release-notes-dialog-title"
    >
      <DialogTitle id="release-notes-dialog-title">
        Notas de versão
      </DialogTitle>
      <DialogContent dividers ref={contentRef}>
        {notes.length === 0 && (
          <Typography color="text.secondary">Nenhuma nota de versão encontrada.</Typography>
        )}

        {notes.map((item) => (
          <ReleaseNotesContent key={item.version} notes={item} current={item.version === version} />
        ))}

        {hasMore && (
          <Box ref={sentinelRef} display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" variant="contained">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ReleaseNotesModal.defaultProps = {
  version: appVersion
};
